import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Clock, Users, MapPin, Sparkles, ArrowRight } from 'lucide-react';

const Tours = () => {
  const { t } = useLanguage();

  const toursList = [
    {
      key: 'oneHour',
      icon: Clock,
      color: 'from-sky-400 to-sky-500',
      iconBg: 'bg-sky-100',
      iconColor: 'text-sky-600',
      image: '/assets/tour1-hero.jpg',
      price: 'from €30'
    },
    {
      key: 'twoHours',
      icon: Users,
      color: 'from-emerald-400 to-emerald-500',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      image: '/assets/tour2-hero.jpg',
      price: 'from €45'
    },
    {
      key: 'threeHours',
      icon: MapPin,
      color: 'from-violet-400 to-violet-500',
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
      image: '/assets/tour3-hero.jpg',
      price: 'from €75'
    }
  ];

  return (
    <section id="tours" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-amber-100 rounded-full mb-4">
            <Sparkles className="w-5 h-5 text-amber-600" />
            <span className="text-amber-800 font-semibold text-sm uppercase tracking-wide">Premium Tours</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.tours.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t.tours.subtitle}
          </p>
        </div>

        {/* Tours Grid - Horizontal scroll on mobile, Grid on desktop */}
        <div className="w-full">
          {/* Mobile: Horizontal Scroll */}
          <div className="md:hidden overflow-x-auto pb-8 -mx-4 px-4">
            <div className="flex gap-6 snap-x snap-mandatory">
              {toursList.map((tour, index) => {
                const tourData = t.tours[tour.key];
                
                return (
                  <div 
                    key={tour.key}
                    className="flex-shrink-0 w-[85vw] snap-center group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className={`h-2 bg-gradient-to-r ${tour.color}`} />
                    
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={tour.image}
                        alt={tourData.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                      
                      <div className="absolute top-4 left-4 bg-[#E85D4A] px-4 py-2 rounded-lg shadow-lg">
                        <div className="flex items-baseline space-x-1">
                          <span className="text-white text-xs">from</span>
                          <span className="text-white font-bold text-lg">{tour.price.replace('from ', '')}</span>
                        </div>
                        <div className="text-white/90 text-xs mt-1 font-normal">per person</div>
                      </div>
                    </div>
                    
                    <div className="p-6 flex flex-col flex-grow">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        {tourData.title}
                      </h3>

                      <p className="text-gray-600 mb-6 leading-relaxed flex-grow whitespace-pre-line">
                        {tourData.description}
                      </p>

                      <Link
                        to={`/tour-${tour.key}`}
                        className={`w-full mt-auto group/btn flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r ${tour.color} text-white font-semibold rounded-lg transition-all duration-300 shadow-md`}
                      >
                        <span>{t.tours.exploreMore}</span>
                        <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Desktop: Grid Layout */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {toursList.map((tour, index) => {
            const tourData = t.tours[tour.key];
            
            return (
              <div 
                key={tour.key}
                className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:-translate-y-2 flex flex-col"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`h-2 bg-gradient-to-r ${tour.color}`} />
                
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={tour.image}
                    alt={tourData.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  
                  <div className="absolute top-4 left-4 bg-[#E85D4A] px-4 py-2 rounded-lg shadow-lg">
                    <div className="flex items-baseline space-x-1">
                      <span className="text-white text-xs">from</span>
                      <span className="text-white font-bold text-lg">{tour.price.replace('from ', '')}</span>
                    </div>
                    <div className="text-white/90 text-xs mt-1 font-normal">per person</div>
                  </div>
                </div>
                
                <div className="p-8 flex flex-col flex-grow">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {tourData.title}
                  </h3>

                  <p className="text-gray-600 mb-6 leading-relaxed flex-grow whitespace-pre-line">
                    {tourData.description}
                  </p>

                  <Link
                    to={`/tour-${tour.key}`}
                    className={`w-full mt-auto group/btn flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r ${tour.color} text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg`}
                  >
                    <span>{t.tours.exploreMore}</span>
                    <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                  </Link>
                </div>

                <div className={`h-1 bg-gradient-to-r ${tour.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300`} />
              </div>
            );
          })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Tours;
